import styled from '@emotion/styled'
import { Primary500 } from '@mehilainen/mds-customer/colors'
import { AngleRight } from '@mehilainen/mds-customer/icons'
import { spacing } from '@mehilainen/mds-tokens/spacing'
import { Button } from '@mui/material'
import React, { useEffect, useRef } from 'react'
import { useTranslation } from 'react-i18next'

import { AppointmentSearchResult, AppointmentType } from '../../../__generated__/api'
import { AppointmentSearchMode } from '../../../state/search/atoms'
import { useIsMobile, useIsTablet } from '../../hooks/useBreakpoint'
import { breakpoint } from '../../utils/breakpoint'
import dayjs from '../../utils/dayjs/dayjs'
import { PrimaryHover } from '../DefaultButton/DefaultButton'
import { ColumnFlex } from '../Layout/Layout'

import AppointmentLocationDetails from './AppointmentLocationDetails'
import AppointmentSpecialistDetails from './AppointmentSpecialistDetails'
import AppointmentTimeDetails from './AppointmentTimeDetails'
import OHCStamp from './OHCStamp'
import {
  BaseTile,
  CallbackColumnGrid,
  FourColumnGrid,
  FourColumnGridWithInfoText,
  MobileBaseTile,
  ThreeColumnGrid,
} from './styles'
import { buildAriaLabelForAppointment } from './utils'

const MobileContentRow = styled.div`
  display: flex;
  align-items: center;
  gap: ${spacing[300].rem};
  width: 100%;
`

const MobileColumnFlex = styled(ColumnFlex)`
  flex: 1;
`

const DesktopBaseTile = styled(BaseTile)<{
  hideSpecialistDetails: boolean
  isCallback: boolean
  highPadding: boolean
  hasInfoText: boolean
}>`
  padding: ${spacing[500].rem};
  padding-bottom: ${(props) => (props.highPadding ? spacing[700].rem : spacing[500].rem)};

  ${(props) => {
    if (props.isCallback) return CallbackColumnGrid
    if (props.hideSpecialistDetails) return ThreeColumnGrid
    if (props.hasInfoText) return FourColumnGridWithInfoText
    return FourColumnGrid
  }}

  ${AppointmentTimeDetails}, ${AppointmentLocationDetails} {
    @media (min-width: ${breakpoint.sm}px) {
      align-self: center;
    }
  }

  &:focus-within {
    border-color: ${Primary500};
  }
`
const StyledButton = styled(Button)`
  align-self: flex-start;
  ${PrimaryHover}
`

const ChevronWrapper = styled.div`
  align-self: flex-start;
  margin-right: -${spacing[400].rem};
`

interface Props {
  appointment: AppointmentSearchResult
  hideSpecialistDetails?: 'hide-both' | 'hide-mobile' | 'false'
  onSpecialistClick?(): void
  onClick?(): void
  remoteSupported: boolean
  autoFocus?: boolean
}

const TimeslotTile: React.FC<Props> = ({
  appointment,
  hideSpecialistDetails = 'false',
  onSpecialistClick,
  onClick,
  remoteSupported,
  autoFocus,
}) => {
  const { t } = useTranslation()
  const isMobile = useIsMobile()
  const isTablet = useIsTablet()
  const buttonRef = useRef<HTMLLIElement>(null)

  const appointmentDate = dayjs(appointment.time)
  const isCallbackAppointment = appointment.appointmentTypes?.includes(
    AppointmentSearchMode.CALLBACK
  )
  const isRemoteAppointment = !appointment.appointmentTypes?.includes(AppointmentType.Clinic)
  const isAlsoRemoteAppointment =
    appointment.appointmentTypes?.includes(AppointmentType.Clinic) &&
    appointment.appointmentTypes?.length > 1

  const StampComponent = OHCStamp({ appointment })
  const hasStamp = StampComponent !== null

  const ariaLabel = buildAriaLabelForAppointment({
    t,
    appointment,
    appointmentDate,
    isCallbackAppointment,
    isRemoteAppointment,
    isAlsoRemoteAppointment,
  })

  useEffect(() => {
    if (autoFocus) {
      buttonRef.current?.focus()
    }
  }, [autoFocus])

  const commonTileProps = {
    role: 'button' as const,
    tabIndex: 0,
    onClick,
    onKeyPress: (event: React.KeyboardEvent) => event.key === 'Enter' && onClick?.(),
    'data-cy': isMobile ? 'appointmentTile-reserve' : 'appointmentTile',
    ref: buttonRef,
  }

  const timeDetailsProps = {
    date: appointmentDate,
    duration: appointment.hasFlexibleScheduleOptions ? null : appointment.duration,
    isCallbackAppointment,
    isMobile,
  }

  const locationDetailsProps = {
    locationName: appointment.locationName,
    locationShortName: appointment.locationShortName,
    locationAddress: appointment.locationAddress,
    appointmentTypes: appointment.appointmentTypes as AppointmentType[],
    remoteSupported,
    date: appointmentDate,
  }

  const specialistDetailsProps = {
    name: appointment.specialistName,
    title: appointment.specialistTitle,
    isResourceList: appointment.isResourceList,
    image: appointment.image,
    allowsNewKelaClients: appointment.specialistAllowsNewKelaClients,
  }

  const showSpecialistDetails =
    hideSpecialistDetails !== 'hide-both' && (!isMobile || hideSpecialistDetails !== 'hide-mobile')

  const hasInfoText = appointment.specialistAllowsNewKelaClients === false

  if (isMobile) {
    return (
      <MobileBaseTile {...commonTileProps} highPadding={hasStamp}>
        <AppointmentTimeDetails {...timeDetailsProps} variant="S" isMobile />
        <MobileContentRow>
          {showSpecialistDetails && (
            <MobileColumnFlex>
              <AppointmentSpecialistDetails
                {...specialistDetailsProps}
                variant="XS"
                extraElement={<AppointmentLocationDetails {...locationDetailsProps} variant="S" />}
              />
            </MobileColumnFlex>
          )}
          <ChevronWrapper>
            <AngleRight htmlColor={Primary500} fontSize="large" />
          </ChevronWrapper>
        </MobileContentRow>
        {StampComponent}
      </MobileBaseTile>
    )
  }

  return (
    <DesktopBaseTile
      {...commonTileProps}
      hideSpecialistDetails={hideSpecialistDetails === 'hide-both'}
      isCallback={isCallbackAppointment}
      highPadding={hasStamp}
      hasInfoText={hasInfoText}
    >
      <AppointmentTimeDetails
        {...timeDetailsProps}
        variant={isTablet ? 'S' : 'M'}
        isMobile={false}
      />
      {showSpecialistDetails && (
        <AppointmentSpecialistDetails
          {...specialistDetailsProps}
          onClick={onSpecialistClick}
          variant={isTablet ? 'S' : 'L'}
        />
      )}
      <AppointmentLocationDetails {...locationDetailsProps} variant={isTablet ? 'S' : 'M'} />
      <StyledButton data-cy="appointmentTile-reserve" aria-label={ariaLabel}>
        {t('component.appointmentList.reserveButtonText')}
      </StyledButton>
      {StampComponent}
    </DesktopBaseTile>
  )
}

export default TimeslotTile
